import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import Joyride, { CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProductTour } from '../../contexts/ProductTourContext';
import { useSuggestionWizard } from '../../contexts/SuggestionWizardContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { tourSteps } from '../../constants/tourSteps';
import { getPageForStep, STEP_INDICES, TOUR_SAMPLE_DATA } from '../../constants/tour';
import { TourOverlay } from './TourOverlay';
import { TourTooltip } from './TourTooltip';
import { isMobileDevice, getEffectiveStepProps, waitForElement, scrollElementIntoViewMobile } from './tourUtils';

// Check if a step should be shown on current device
const shouldShowStep = (stepIndex: number): boolean => {
  const step = tourSteps[stepIndex];
  if (!step) return false;
  const isMobile = isMobileDevice();
  if (isMobile && step.hideOnMobile) return false;
  if (!isMobile && step.hideOnDesktop) return false;
  return true;
};

// Find the next visible step index
const findNextVisibleStep = (fromIndex: number, direction: 1 | -1 = 1): number => {
  let nextIndex = fromIndex + direction;
  while (nextIndex >= 0 && nextIndex < tourSteps.length) {
    if (shouldShowStep(nextIndex)) return nextIndex;
    nextIndex += direction;
  }
  return direction === 1 ? tourSteps.length - 1 : 0;
};

export const ProductTour: React.FC = () => {
  const { isActive, currentStep, setStep, nextStep, endTour, skipTour } = useProductTour();
  const { closeWizard } = useSuggestionWizard();
  const { closeSettings } = useSettingsModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [elementReady, setElementReady] = useState(false);
  const clickListenerRef = useRef<(() => void) | null>(null);
  const prevStepRef = useRef<number>(currentStep);

  // Transform steps with device-aware props (but keep all steps, don't filter)
  // Only include Joyride-compatible properties to avoid confusion from custom mobile props
  const transformedSteps = useMemo(() => {
    return tourSteps.map(step => {
      const props = getEffectiveStepProps(step);
      return {
        name: step.name,
        target: props.target,
        title: props.title,
        content: props.content,
        placement: props.placement,
        disableBeacon: step.disableBeacon,
        spotlightClicks: step.spotlightClicks,
        spotlightPadding: step.spotlightPadding,
      };
    });
  }, []);

  // Auto-skip hidden steps
  useEffect(() => {
    if (!isActive) return;
    if (!shouldShowStep(currentStep)) {
      // Skip to next visible step
      const nextVisible = findNextVisibleStep(currentStep, 1);
      setStep(nextVisible);
    }
  }, [isActive, currentStep, setStep]);

  // Handle transitions when leaving certain steps
  useEffect(() => {
    const prevStep = prevStepRef.current;
    const prevStepName = tourSteps[prevStep]?.name;
    const currentStepName = tourSteps[currentStep]?.name;

    if (currentStep !== prevStep) {
      // Close suggestion wizard when leaving that step
      if (prevStepName === 'suggestion-wizard') {
        closeWizard();
      }

      // Exit confirmation view when leaving query-processing to show recent datasets
      if (prevStepName === 'query-processing') {
        window.dispatchEvent(new CustomEvent('tour-exit-confirmation-view'));
      }

      // Close settings modal when entering tour-complete step
      if (currentStepName === 'tour-complete') {
        closeSettings();
      }

      // Close chat panel when entering lead-table step on mobile
      if (currentStepName === 'lead-table' && isMobileDevice()) {
       setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tour-close-chat-panel'));
       }, 100);
      }
    }

    prevStepRef.current = currentStep;
  }, [currentStep, closeWizard, closeSettings]);

  // Check if we're on the right page for current step
  useEffect(() => {
    if (!isActive) { setRun(false); return; }
    const stepPage = getPageForStep(currentStep);
    const isOnDataset = location.pathname.includes('/datasets/');

    // Don't redirect during transition steps (like navigate-home)
    if (stepPage === 'transition') return;

    const shouldBeOnDataset = stepPage === 'dataset';

    if (shouldBeOnDataset && !isOnDataset) {
      navigate(`/datasets/${TOUR_SAMPLE_DATA.datasetId}`);
    } else if (!shouldBeOnDataset && isOnDataset && currentStep > STEP_INDICES.RECENT_DATASETS) {
      navigate('/datasets');
    }
  }, [isActive, currentStep, location.pathname, navigate]);

  // Wait for target element before running
  useEffect(() => {
    if (!isActive) { setElementReady(false); return; }
    const step = transformedSteps[currentStep];
    if (!step || !shouldShowStep(currentStep)) return;

    setRun(false);
    setElementReady(false);
    const targetSelector = step.target as string;
    // Longer timeout for steps that depend on async operations or page transitions
    const currentStepName = tourSteps[currentStep]?.name;
    const stepPage = getPageForStep(currentStep);
    let timeout = 5000;
    if (currentStepName === 'query-processing') {
      timeout = 15000; // Backend query processing
    } else if (stepPage === 'dataset' && currentStepName === 'lead-table') {
      timeout = 10000; // First step on dataset page after navigation
    }

    // Add initial delay for page transitions to dataset page
    const initialDelay = stepPage === 'dataset' && currentStepName === 'lead-table' ? 500 : 50;

    let cancelled = false;
    const timerId = setTimeout(() => {
      if (cancelled) return;
      waitForElement(targetSelector, timeout).then(el => {
        if (cancelled) return;
        if (el) {
          // Scroll element into view on mobile before showing the tour step
          scrollElementIntoViewMobile(targetSelector);
          // Small delay to allow scroll to complete on mobile
          const scrollDelay = isMobileDevice() ? 300 : 0;
          setTimeout(() => {
            if (cancelled) return;
            setElementReady(true);
            setTimeout(() => {
              if (!cancelled) setRun(true);
            }, 150);
          }, scrollDelay);
        } else {
          // Target element not found after timeout - skip to next step or end tour
          console.warn(`Tour step "${currentStepName}" target not found: ${targetSelector}`);
          const nextVisible = findNextVisibleStep(currentStep, 1);
          if (nextVisible > currentStep && nextVisible < tourSteps.length) {
            setStep(nextVisible);
          } else {
            // No more steps available, end tour
            endTour();
          }
        }
      });
    }, initialDelay);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [isActive, currentStep, transformedSteps]);

  // Handle escape key to close tour
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        skipTour();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, skipTour]);

  // Handle click listeners for requiresClick steps
  useEffect(() => {
    // Clean up previous listener
    if (clickListenerRef.current) {
      clickListenerRef.current();
      clickListenerRef.current = null;
    }

    if (!isActive || !run) return;

    const currentTourStep = tourSteps[currentStep];
    if (!currentTourStep) return;

    const effectiveProps = getEffectiveStepProps(currentTourStep);
    if (!effectiveProps.requiresClick) return;

    const targetSelector = effectiveProps.target;
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const handleClick = () => {
      // Small delay to allow the clicked action to complete
      setTimeout(() => {
        nextStep();
      }, 300);
    };

    // Add click listener to target and its children
    targetElement.addEventListener('click', handleClick, true);

    // Store cleanup function
    clickListenerRef.current = () => {
      targetElement.removeEventListener('click', handleClick, true);
    };

    return () => {
      if (clickListenerRef.current) {
        clickListenerRef.current();
        clickListenerRef.current = null;
      }
    };
  }, [isActive, run, currentStep, nextStep]);

  const handleCallback = useCallback((data: CallBackProps) => {
    const { action, status, type, index } = data;

    // Get current step's requiresClick status
    const currentTourStep = tourSteps[index];
    const effectiveProps = currentTourStep ? getEffectiveStepProps(currentTourStep) : null;
    const requiresClick = effectiveProps?.requiresClick;

    // Don't handle NEXT action for requiresClick steps via Joyride - we handle it via click listener
    // Only advance on STEP_AFTER (explicit user action), NOT on TARGET_NOT_FOUND
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT && !requiresClick) {
        // Find next visible step
        const nextVisible = findNextVisibleStep(index, 1);
        setStep(nextVisible);
      } else if (action === ACTIONS.PREV) {
        // Find previous visible step
        const prevVisible = findNextVisibleStep(index, -1);
        setStep(prevVisible);
      }
    }
    if (status === STATUS.FINISHED) endTour();
    if (status === STATUS.SKIPPED) skipTour();
  }, [setStep, endTour, skipTour]);

  if (!isActive || !elementReady) return null;

  const currentTourStep = tourSteps[currentStep];
  const effectiveProps = currentTourStep ? getEffectiveStepProps(currentTourStep) : null;

  return (
    <>
      <style>{`
        body.joyride-active { overflow: hidden !important; }
        .__floater { z-index: 10001 !important; }
        @media (max-width: 768px) {
          .__floater { width: calc(100% - 16px) !important; max-width: 340px !important; }
          .__floater__arrow { display: none !important; }
        }
      `}</style>
      <TourOverlay currentStep={currentStep} run={run} allowClicks={Boolean(effectiveProps?.requiresClick)} />
      <Joyride
        key={`tour-step-${currentStep}`}
        steps={transformedSteps}
        run={run}
        stepIndex={currentStep}
        continuous
        showProgress
        showSkipButton
        spotlightClicks={effectiveProps?.requiresClick || false}
        disableOverlay
        disableCloseOnEsc
        disableScrolling={!isMobileDevice()}
        scrollToFirstStep
        callback={handleCallback}
        tooltipComponent={TourTooltip}
        floaterProps={{ disableAnimation: true, offset: 8, styles: { arrow: { length: 8, spread: 12 } } }}
        styles={{
          options: { arrowColor: '#fff', backgroundColor: '#fff', primaryColor: '#111827', textColor: '#374151', zIndex: 10000 },
          beacon: { display: 'none' },
        }}
      />
    </>
  );
};
